// Collapsible tool-call panel — shows tool name, input summary, output,
// with syntax highlighting for file tools and diff view for edits.
// Screenshot/capture tools additionally render an inline image or video
// preview so the user sees the result without waiting for the model to reply.
//
// Status indicator system (icon · color · meaning):
//   ⏳  blue/primary (pulse)   — running / pending
//   ✓   green/emerald          — completed successfully
//   ⚠   amber/yellow           — warning: completed with issues
//                                 (non-zero exit, failed checks, boundary violation)
//   ✕   red/destructive        — error: tool call failed
//   ⏳  violet (pulse)         — sub-agent in progress (SubAgentCard)
//   ✓   green/emerald          — sub-agent completed (SubAgentCard)
//   ✕   red/destructive        — sub-agent failed (SubAgentCard)
import { useEffect, useState } from 'react';
import type { StreamSegment } from '@/lib/chatStore';
import { cn } from '@onestone/ui/utils';
import { formatDuration } from '@/lib/format';
import { detectLanguage, highlightCode } from '@/lib/shikiHighlighter';
import { computeDiff } from '@/lib/diffComputer';
import { Markdown } from './Markdown';
import { MediaEmbed, parseMediaMarkers } from './MediaEmbed';
import {
  displayToolName,
  extractBashCommand,
  extractEditStrings,
  extractFilePath,
  shortPath,
  summarizeToolInput,
} from '@onestone/shared/toolCallParsers';
import { TASK_LABEL_MAX_CHARS } from '@/lib/uiConstants';
import { parseSubAgentResult } from '@onestone/shared/subAgentResult';
import { MEDIA_TOOL_NAMES } from '@onestone/shared/tools/media';

// Sub-agent detection — spawn_agent gets a dedicated card instead of the
// generic tool panel (AG-UI research: dedicated activity panel + progressive
// disclosure for multi-step autonomous work).
// Match both raw `spawn_agent` (SDK path) and `mcp__atlas__spawn_agent`
// (CLI/subscription path) — the MCP namespace prefix varies by executor.
const SUB_AGENT_NAMES = new Set(['spawn_agent', 'mcp__atlas__spawn_agent']);

// Post-Phase-1.5 (2026-06-30): only the OneStone-canonical snake_case names
// reach the renderer. CLI-cased aliases (Read/Write/Edit/Bash) were dropped
// when the CLI built-in surface was unified into MCP.
const FILE_READ_TOOLS = new Set(['read_file']);
const FILE_WRITE_TOOLS = new Set(['write_file']);
const FILE_EDIT_TOOLS = new Set(['edit_file']);
const SHELL_TOOLS = new Set(['bash']);
// Tools whose output is markdown — render via Markdown instead of <pre>.
// skill_load returns full skill body; memory_get returns full entry content.
const MARKDOWN_OUTPUT_TOOLS = new Set(['skill_load', 'memory_get']);

// Media in a tool result arrives as [SEND_PHOTO|VIDEO|DOCUMENT:path|caption]
// markers (parseMediaMarkers), read only from the tools that emit them
// (MEDIA_TOOL_NAMES) — a grep or read_file result quoting a marker is just
// text, not an embed of a path. A tool whose job is showing a file to the
// operator gets its embed below the panel, always visible; any other tool's
// media (screenshots, recordings) previews inside the expanded panel.
const DISPLAY_TOOLS = new Set(['show_media']);

/** Highlighted code block (async Shiki). Plain text until `active`: a chat holds
 *  many collapsed tool panels, and highlighting the hidden ones loaded the
 *  highlighter on every page load (MA-461). Once highlighted, the result stays. */
function HighlightedCode({ code, filePath, active }: { code: string; filePath: string | null; active: boolean }) {
  const [html, setHtml] = useState<string | null>(null);
  // Latches on first open, so collapsing and reopening doesn't highlight again.
  const [wanted, setWanted] = useState(active);
  if (active && !wanted) setWanted(true);
  const lang = filePath ? detectLanguage(filePath) : null;

  useEffect(() => {
    if (!wanted || !lang || code.length > 50_000) return;
    let cancelled = false;
    highlightCode(code, lang)
      .then((h) => {
        if (!cancelled) setHtml(h);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [wanted, code, lang]);

  if (html) {
    return (
      <div
        className="max-h-80 overflow-auto rounded-md text-xs [&_pre]:!bg-transparent [&_pre]:!p-2 [&_code]:!text-xs"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md bg-background/60 p-2 font-mono text-xs">
      {code}
    </pre>
  );
}

/** Diff view for edit_file operations. */
function DiffView({ diff }: { diff: ReturnType<typeof computeDiff>; filePath: string | null }) {
  return (
    <div className="max-h-80 overflow-auto rounded-md bg-background/60 p-2 font-mono text-xs">
      <div className="mb-1 text-muted-foreground">
        <span className="text-emerald-500">+{diff.addedCount}</span>{' '}
        <span className="text-red-400">-{diff.removedCount}</span>
      </div>
      {diff.lines.map((line, i) => (
        <div
          key={i}
          className={cn(
            'whitespace-pre-wrap',
            line.kind === 'added' && 'bg-emerald-500/10 text-emerald-400',
            line.kind === 'removed' && 'bg-red-500/10 text-red-400 line-through',
            line.kind === 'unchanged' && 'text-muted-foreground',
          )}
        >
          <span className="inline-block w-5 select-none text-right opacity-40">
            {line.kind === 'added' ? '+' : line.kind === 'removed' ? '-' : ' '}
          </span>{' '}
          {line.content}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-agent dedicated card — visually distinct from regular tool calls.
// AG-UI research: dedicated activity panel + progressive disclosure for
// multi-step autonomous work.
// ---------------------------------------------------------------------------

function SubAgentCard({ segment }: { segment: Extract<StreamSegment, { type: 'tool_call' }> }) {
  const pending = segment.output === undefined;
  const errored = segment.isError === true;
  const liveTools = segment.subAgentTools ?? [];

  // Parse the task from input
  const taskMatch = segment.input.match(/"task"\s*:\s*"([^"]+)"/);
  const task = taskMatch ? (taskMatch[1] ?? '').replace(/\\n/g, ' ').slice(0, TASK_LABEL_MAX_CHARS) : 'Working…';
  const modelMatch = segment.input.match(/"model"\s*:\s*"([^"]+)"/);
  const model = modelMatch ? modelMatch[1] : null;

  // The dispatcher's header (counts, partial) and the report after it.
  const parsed = segment.output !== undefined ? parseSubAgentResult(segment.output) : undefined;
  const toolCalls = parsed?.header?.toolCalls ?? null;
  const tokens = parsed?.header?.tokens.toLocaleString() ?? null;
  const partial = parsed?.header?.partial === true;
  const cleanOutput = parsed?.report ?? '';

  // Live progress: count completed vs in-flight
  const completedTools = liveTools.filter((t) => t.output !== undefined).length;
  const activeTools = liveTools.filter((t) => t.output === undefined);
  const activeTool = activeTools[activeTools.length - 1];

  return (
    <details
      open={pending}
      className={cn(
        'group rounded-lg border-2 text-xs',
        errored
          ? 'border-destructive/40 bg-destructive/5'
          : pending
            ? 'border-violet-500/40 bg-violet-500/5'
            : 'border-violet-500/20 bg-violet-500/5',
      )}
    >
      {/* Summary — always visible, clickable to expand/collapse */}
      <summary
        className={cn(
          'flex cursor-pointer list-none items-center gap-2 px-3 py-2 transition-colors hover:bg-violet-500/10',
          pending ? 'text-violet-500' : errored ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400',
        )}
      >
        <span className="inline-block transition-transform group-open:rotate-90" aria-hidden="true">
          &#9656;
        </span>
        <span
          aria-hidden="true"
          className={cn(
            'text-xs',
            errored ? 'text-destructive' : pending ? 'animate-pulse text-violet-500' : 'text-emerald-500',
          )}
          title={
            errored
              ? 'Sub-agent failed'
              : pending
                ? 'Sub-agent running…'
                : partial
                  ? 'Sub-agent stopped at its step limit — partial report'
                  : 'Sub-agent completed'
          }
        >
          {errored ? '✕' : pending ? '⏳' : '✓'}
        </span>
        <span className="font-semibold text-foreground">Sub-agent</span>
        {model && (
          <code className="rounded bg-violet-500/10 px-1.5 py-0.5 font-mono text-xs text-violet-600 dark:text-violet-400">
            {model}
          </code>
        )}
        {pending && liveTools.length > 0 && (
          <span className="font-mono text-xs text-violet-500">
            {completedTools}/{liveTools.length} tools
          </span>
        )}
        {pending && liveTools.length === 0 && <span className="animate-pulse text-xs text-violet-500">starting…</span>}
        {!pending && !errored && toolCalls !== null && (
          <span className="ml-auto flex items-center gap-2 font-mono text-xs text-muted-foreground">
            {partial && <span className="text-amber-600 dark:text-amber-400">partial</span>}
            <span>
              {toolCalls} tool{toolCalls !== 1 ? 's' : ''}
            </span>
            <span className="text-violet-500/60">·</span>
            <span>{tokens} tok</span>
            <span className="text-violet-500/60">·</span>
            <span>{formatDuration(segment.durationMs ?? 0)}</span>
          </span>
        )}
        {!pending && !errored && toolCalls === null && (
          <span className="ml-auto font-mono text-xs text-muted-foreground">
            {formatDuration(segment.durationMs ?? 0)}
          </span>
        )}
        {errored && <span className="ml-auto font-mono text-xs text-destructive">failed</span>}
      </summary>

      {/* Body — collapsible via <details> */}
      <div className="flex flex-col border-t border-violet-500/10">
        {/* Task description */}
        <div className="px-3 py-1.5">
          <p className="text-xs text-muted-foreground italic">{task}</p>
        </div>

        {/* Live tool activity — shown while sub-agent is running */}
        {pending && liveTools.length > 0 && (
          <div className="border-t border-violet-500/10 px-3 py-1.5">
            <div className="space-y-1">
              {liveTools.map((tool) => {
                const toolDone = tool.output !== undefined;
                const toolErr = tool.isError === true;
                return (
                  <div key={tool.id} className="flex items-center gap-1.5 font-mono text-xs">
                    <span className={cn('text-2xs', !toolDone && 'animate-pulse text-violet-500')}>
                      {toolErr ? '✕' : toolDone ? '✓' : '⏳'}
                    </span>
                    <span
                      className={cn(
                        'font-semibold',
                        toolErr ? 'text-destructive' : toolDone ? 'text-muted-foreground' : 'text-foreground',
                      )}
                    >
                      {displayToolName(tool.name)}
                    </span>
                    {tool.input && (
                      <span className="max-w-[200px] truncate text-muted-foreground">
                        {summarizeToolInput(tool.input) ?? ''}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Currently active tool — show what's happening right now */}
            {activeTool && (
              <div className="mt-1 flex items-center gap-1 text-2xs text-violet-500">
                <span className="animate-pulse">●</span>
                <span>{displayToolName(activeTool.name)}</span>
                <span className="text-muted-foreground">running…</span>
              </div>
            )}
          </div>
        )}

        {/* Output */}
        {!pending && cleanOutput && !errored && (
          <div className="max-h-80 overflow-auto border-t border-violet-500/10 px-3 py-2">
            <Markdown content={cleanOutput} />
          </div>
        )}

        {/* Error output */}
        {errored && segment.output && (
          <div className="border-t border-violet-500/10 px-3 py-2">
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-destructive">
              {segment.output}
            </pre>
          </div>
        )}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Generic tool call panel
// ---------------------------------------------------------------------------

export function ToolCallDetails({ segment }: { segment: Extract<StreamSegment, { type: 'tool_call' }> }) {
  // React mounts a closed <details>' children: a mounted MediaEmbed downloads
  // its file and a mounted HighlightedCode runs Shiki — so both wait until the
  // panel is open.
  const [expanded, setExpanded] = useState(false);

  // Route spawn_agent to its dedicated card
  if (SUB_AGENT_NAMES.has(segment.name)) {
    return <SubAgentCard segment={segment} />;
  }

  const pending = segment.output === undefined;
  const errored = segment.isError === true;
  const warned = !errored && segment.isWarning === true;
  const toolName = displayToolName(segment.name);
  const filePath = extractFilePath(segment.input);
  const isFileRead = FILE_READ_TOOLS.has(toolName);
  const isFileWrite = FILE_WRITE_TOOLS.has(toolName);
  const isFileEdit = FILE_EDIT_TOOLS.has(toolName);
  const isShell = SHELL_TOOLS.has(toolName);
  const isMarkdownOutput = MARKDOWN_OUTPUT_TOOLS.has(toolName);
  const mediaMarkers =
    segment.output && !errored && MEDIA_TOOL_NAMES.has(toolName) ? parseMediaMarkers(segment.output).markers : [];
  const showsOpenly = DISPLAY_TOOLS.has(toolName);
  const editStrings = isFileEdit ? extractEditStrings(segment.input) : null;
  const bashCommand = isShell ? extractBashCommand(segment.input) : null;
  const editDiff = editStrings ? computeDiff(editStrings.oldStr, editStrings.newStr) : null;
  // Derived one-line preview for tools without a dedicated chip (memory, skills,
  // web, analytics…). Never the tool name — that's already shown bold.
  const inputPreview = filePath || bashCommand ? null : summarizeToolInput(segment.input);

  const panel = (
    <details
      open={pending}
      onToggle={(e) => setExpanded(e.currentTarget.open)}
      className={cn(
        'group rounded-lg border text-xs',
        errored
          ? 'border-destructive/30 bg-destructive/5'
          : warned
            ? 'border-amber-500/30 bg-amber-500/5'
            : pending
              ? 'border-primary/30 bg-primary/5'
              : 'border-emerald-500/20 bg-emerald-500/5',
      )}
    >
      <summary
        className={cn(
          'flex cursor-pointer list-none items-center gap-1.5 px-2.5 py-1.5 transition-colors hover:bg-muted/40',
          errored
            ? 'text-destructive'
            : warned
              ? 'text-amber-600 dark:text-amber-400'
              : pending
                ? 'text-muted-foreground'
                : 'text-emerald-600 dark:text-emerald-400',
        )}
      >
        <span className="inline-block transition-transform group-open:rotate-90" aria-hidden="true">
          &#9656;
        </span>
        <span
          aria-hidden="true"
          className={cn(
            'text-xs',
            errored
              ? 'text-destructive'
              : warned
                ? 'text-amber-500'
                : pending
                  ? 'animate-pulse text-primary'
                  : 'text-emerald-500',
          )}
          title={
            errored
              ? 'Error — tool call failed'
              : warned
                ? 'Warning — completed with issues (non-zero exit, failed checks, or boundary violation)'
                : pending
                  ? 'Running…'
                  : 'Success'
          }
        >
          {errored ? '✕' : warned ? '⚠' : pending ? '⏳' : '✓'}
        </span>
        <strong className="font-semibold text-foreground">{toolName}</strong>
        {filePath ? (
          <code className="max-w-[260px] truncate rounded bg-muted px-1 py-0.5 font-mono text-xs text-primary/80">
            {shortPath(filePath)}
          </code>
        ) : bashCommand ? (
          <code className="max-w-[280px] truncate rounded bg-muted px-1 py-0.5 font-mono text-xs text-primary/80">
            $ {bashCommand}
          </code>
        ) : inputPreview ? (
          <code className="max-w-[280px] truncate rounded bg-muted px-1 py-0.5 font-mono text-xs">{inputPreview}</code>
        ) : null}
        {editDiff ? (
          <span className="font-mono text-xs">
            <span className="text-emerald-500">+{editDiff.addedCount}</span>{' '}
            <span className="text-red-400">−{editDiff.removedCount}</span>
          </span>
        ) : null}
        <span
          className={cn(
            'ml-auto font-mono text-xs',
            pending
              ? 'animate-pulse text-primary'
              : errored
                ? 'text-destructive'
                : warned
                  ? 'text-amber-500'
                  : 'text-emerald-500/70',
          )}
        >
          {pending ? 'running…' : formatDuration(segment.durationMs ?? 0)}
        </span>
      </summary>

      <div className="flex flex-col gap-2 border-t border-border/60 px-2.5 py-2">
        {/* Input section — show diff for edits, path for reads/writes, raw for others */}
        {isFileEdit && editDiff ? (
          <div>
            <div className="mb-0.5 font-mono text-xs uppercase tracking-wider text-muted-foreground">
              diff {filePath ? `— ${filePath.split('/').pop()}` : ''}
            </div>
            <DiffView diff={editDiff} filePath={filePath} />
          </div>
        ) : segment.input.length > 0 ? (
          <div>
            <div className="mb-0.5 font-mono text-xs uppercase tracking-wider text-muted-foreground">input</div>
            {isFileWrite && filePath ? (
              <HighlightedCode code={segment.input} filePath={filePath} active={expanded} />
            ) : (
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-md bg-background/60 p-2 font-mono text-xs">
                {segment.input}
              </pre>
            )}
          </div>
        ) : null}

        {/* Output section — syntax highlight file reads and shell output */}
        {segment.output !== undefined ? (
          <div>
            <div className="mb-0.5 font-mono text-xs uppercase tracking-wider text-muted-foreground">output</div>
            {errored ? (
              <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md bg-destructive/10 p-2 font-mono text-xs text-destructive">
                {segment.output.length > 0 ? segment.output : '(empty)'}
              </pre>
            ) : warned ? (
              <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md bg-amber-500/10 p-2 font-mono text-xs text-amber-700 dark:text-amber-300">
                {segment.output.length > 0 ? segment.output : '(empty)'}
              </pre>
            ) : isFileRead && filePath ? (
              <HighlightedCode code={segment.output} filePath={filePath} active={expanded} />
            ) : isShell ? (
              <HighlightedCode code={segment.output} filePath="output.sh" active={expanded} />
            ) : isMarkdownOutput && segment.output.length > 0 ? (
              <div className="max-h-80 overflow-auto rounded-md bg-background/60 p-2 text-xs">
                <Markdown content={segment.output} />
              </div>
            ) : (
              <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md bg-background/60 p-2 font-mono text-xs">
                {segment.output.length > 0 ? segment.output : '(empty)'}
              </pre>
            )}

            {/* Capture tools' media previews inside the panel, without waiting
                for the model to reply. */}
            {!showsOpenly && expanded && mediaMarkers.map((m, i) => <MediaEmbed key={i} marker={m} />)}
          </div>
        ) : (
          <div className="text-xs italic text-muted-foreground">waiting for result&hellip;</div>
        )}
      </div>
    </details>
  );

  if (!showsOpenly || mediaMarkers.length === 0) return panel;
  return (
    <>
      {panel}
      {mediaMarkers.map((m, i) => (
        <MediaEmbed key={i} marker={m} />
      ))}
    </>
  );
}
